/**
 * Die Liste „Gelöste Zahlungsbelege“ im Reiter Zahlung (Ticket H, Review W1; vom Nutzer freigegeben): eine reine
 * Download-Liste mit Dateiname und Hochladedatum. Keine Aktionen, und der Abschnitt erscheint nur, wenn es Einträge gibt.
 *
 * Warum es sie gibt: der mit Ticket H entfernte Abschnitt „Überweisungsbelege“ war der einzige Ort, an dem gelöste
 * Zahlungsbelege (`geloesteZahlungsbelege`) herunterladbar waren, die sonst nirgends in der Oberfläche stehen: Dateien, die
 * direkt an eine Ist-Zahlung hochgeladen wurden und nach „Zahlung zurücknehmen“ oder „Zahlstatus zurücksetzen“ an nichts mehr
 * hängen. Sie haben keinen Dokumentlink, stehen also weder in `pdfs` noch im Reiter Dokumente
 * (`backend/src/domaene/zahlungIstLesen.ts`; `docType` ist `anzahlungsbeleg` oder `restzahlungsbeleg`).
 * Der Server liefert nur Dokumente, die weder einen Link noch eine Ist-Zahlung haben und die der Nutzer nicht per
 * „Dokument entfernen“ (Ticket F, `document_link_removals`) von der Bestellung genommen hat; ebenso fehlen Belege,
 * die aus der DSV-Zuordnung stammen (`dsv_beleg_zuordnungen`). Ein entferntes Dokument steht allein unter „Entfernte
 * Dokumente“ im Reiter Dokumente und wird hier nicht zum gelösten Beleg.
 *
 * Früher kam eine zweite Art dazu: Dokumentlinks der Split-Typen aus `pdfs`, die der Reiter Dokumente nie zeichnete. Mit dem
 * Anzahlungs- und dem Restzahlungs-/Gesamtzahlungsbeleg zeichnet die Checkliste dort beide Typen; ein Dokumentlink gehört
 * deshalb dorthin und `pdfs` wird hier nicht mehr gelesen. Ein Beleg, der an einer Ist-Zahlung hängt, steht im Zahlstatus
 * (`IstZahlung.tsx`) und wird hier ausgeblendet.
 *
 * Der Download geht über denselben Weg wie die übrigen Belege (`api/useDateiDownload.ts`): ein gescheiterter Download
 * landet im Fehlerstapel, nicht in einer eigenen Fehlerzeile.
 */
import { Abschnitt } from "../Abschnitt.tsx";
import { useDateiDownloadMitStapel } from "../../../api/useDateiDownload.ts";
import { NUM, fmtDate } from "../../../lib/format.ts";
import type { Order } from "../../../lib/types.ts";
import { BELEG_DOWNLOAD_FEHLGESCHLAGEN_MELDUNG, verknuepfteDokumentIds } from "./IstZahlung.tsx";

interface GeloesteBelegZeile {
  /** `documents.id`; Schlüssel der Zeile und Grundlage der Zusammenführung. */
  documentId: string;
  name: string;
  /** `/api/files/<id>`, wie bei allen Belegen. */
  path: string;
  /** ISO-Zeitstempel des Hochladens; `null`, wenn der Server keinen liefert. */
  uploadedAt: string | null;
}

function zeitwert(zeile: GeloesteBelegZeile): number {
  const t = zeile.uploadedAt ? Date.parse(zeile.uploadedAt) : NaN;
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/**
 * Alle gelösten Belege, die an keiner Ist-Zahlung hängen. Jede Datei einmal, älteste zuerst, ohne Hochladedatum zuletzt.
 */
function geloesteBelegeVon(bestellung: Order): GeloesteBelegZeile[] {
  const verknuepft = verknuepfteDokumentIds(bestellung);
  const zeilen = new Map<string, GeloesteBelegZeile>();
  const aufnehmen = (documentId: unknown, name: unknown, path: unknown, uploadedAt: unknown): void => {
    if (typeof documentId !== "string" || !documentId) return;
    if (verknuepft.has(documentId) || zeilen.has(documentId)) return;
    if (typeof name !== "string" || !name || typeof path !== "string" || !path) return;
    zeilen.set(documentId, { documentId, name, path, uploadedAt: typeof uploadedAt === "string" ? uploadedAt : null });
  };
  for (const g of bestellung.geloesteZahlungsbelege ?? []) aufnehmen(g.documentId, g.name, g.path, g.uploadedAt);
  // `sort` ist stabil: gleiche Zeitwerte (auch mehrere ohne Datum) behalten die Reihenfolge der Quelle.
  return [...zeilen.values()].sort((a, b) => zeitwert(a) - zeitwert(b));
}

interface GeloesteBelegeProps {
  /** Die Bestellung, wie sie ankommt (nicht die für die Anzeige normalisierte): gelesen werden `payments` und `geloesteZahlungsbelege`. */
  bestellung: Order;
}

export function GeloesteBelege({ bestellung }: GeloesteBelegeProps) {
  const { herunterladen } = useDateiDownloadMitStapel(BELEG_DOWNLOAD_FEHLGESCHLAGEN_MELDUNG);
  const zeilen = geloesteBelegeVon(bestellung);
  if (zeilen.length === 0) return null;
  return (
    <Abschnitt schluessel="pay-geloest" titel={`Gelöste Zahlungsbelege · ${NUM.format(zeilen.length)}`}>
      <div className="pay-geloest-list">
        {zeilen.map((zeile) => (
          <div className="doc-row pay-geloest-row" key={zeile.documentId}>
            <div className="doc-ic">{(zeile.name.split(".").pop() || "DOC").toUpperCase().slice(0, 4)}</div>
            <div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  void herunterladen(zeile.path, zeile.name);
                }}
              >
                {zeile.name}
              </a>
              {zeile.uploadedAt ? (
                <span className="pay-geloest-meta">{`hochgeladen am ${fmtDate(zeile.uploadedAt)}`}</span>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </Abschnitt>
  );
}
